import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, filter, switchMap } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import type { UserIdMap } from '@core/account-data';
import { CurrentUserSelectors, UserSelectors } from '@core/account-data';
import type { CurrentUser } from '@core/account-types';
import { filterBoolean } from '@core/common-legacy';
import type { Requisition } from '@core/requisitions-types';
import { RequisitionsSelectors } from '@recruitee/requisitions-data';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';

export type RequisitionsData = {
  requisition: Requisition;
  abilities: RequisitionsAbilities;
  currentUser: CurrentUser;
  users: UserIdMap;
  isDepartmentsMode: boolean;
  isRequisitionReadOnly: boolean;
};

export type RequisitionsAbilities = {
  viewRequisitions: boolean;
  viewAllRequisitions: boolean;
  manageRequisitions: boolean;
  highLevelDataAccess: boolean;
  manageJobs: boolean;
  viewHiredCandidates: boolean;
};

@Injectable()
export class RequisitionsDataProviderService {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);

  public requisition$: Observable<Requisition>;
  public abilities$: Observable<RequisitionsAbilities> = this.store
    .select(AbilitiesSelectors.abilityMap)
    .pipe(
      filterBoolean,
      map(abilities => ({
        viewRequisitions: abilities[Ability.ViewRequisitions],
        viewAllRequisitions: abilities[Ability.ViewAllRequisitions],
        manageRequisitions: abilities[Ability.ManageRequisitions],
        highLevelDataAccess: abilities[Ability.HighLevelDataAccess],
        manageJobs: abilities[Ability.ManageJobs],
        viewHiredCandidates: abilities[Ability.ViewHiredCandidates],
      })),
    );
  public currentUser$ = this.store.select(CurrentUserSelectors.data).pipe(filterBoolean);
  public users$ = this.store.select(UserSelectors.idMap).pipe(filterBoolean);
  public isDepartmentsMode$ = this.store.select(RequisitionsConfigSelectors.isDepartmentsMode);

  public data$: Observable<RequisitionsData>;

  constructor() {
    const route = this.route;
    const store = this.store;

    this.requisition$ = route.params.pipe(
      filter(params => !!params?.id),
      switchMap(params =>
        store.select(RequisitionsSelectors.byIdWithReferences(params.id)).pipe(filterBoolean),
      ),
    );
    this.data$ = combineLatest([
      this.requisition$,
      this.abilities$,
      this.currentUser$,
      this.users$,
      this.isDepartmentsMode$,
    ]).pipe(
      map(([requisition, abilities, currentUser, users, isDepartmentsMode]) => ({
        requisition,
        abilities,
        currentUser,
        users,
        isDepartmentsMode,
        isRequisitionReadOnly: !!requisition.archivedAt,
      })),
      shareReplay(),
    );
  }
}
